import React, { useState, useEffect, useRef } from 'react';
import { FaComments, FaTimes, FaPaperPlane, FaRobot } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown'; // Now that it's installed
import { QUIZMASTER_API } from '../config';

const FloatingChat = ({ documentId, filename, accessToken }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { role: 'assistant', content: `Hi! Ask me anything about **${filename}**.` }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        if (isOpen) scrollToBottom();
    }, [messages, isOpen]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userText = input;
        const newMessages = [...messages, { role: 'user', content: userText }];

        setInput('');
        setMessages(newMessages);
        setIsLoading(true);

        try {
            const response = await fetch(
                `${QUIZMASTER_API}/generate-chat-response/?document_id=${documentId}&enable_ocr=true`,
                {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${accessToken}`
                    },
                    body: JSON.stringify(newMessages.map(m => ({
                        role: m.role,
                        content: m.content
                    })))
                }
            );

            if (response.status === 402) throw new Error('Insufficient wallet balance.');

            const data = await response.json();
            if (!response.ok) throw new Error(data.detail || 'Failed to generate response');

            setMessages(prev => [...prev, {
                role: 'assistant',
                content: data.response
            }]);
        } catch (error) {
            setMessages(prev => [...prev, { role: 'assistant', content: `**Error:** ${error.message}` }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="floating-chat-container">
            {isOpen && (
                <div className="floating-chat-window">
                    <div className="chat-window-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FaRobot />
                            <span style={{ fontWeight: 'bold' }}>Doc Assistant</span>
                        </div>
                        <FaTimes onClick={() => setIsOpen(false)} style={{ cursor: 'pointer' }} />
                    </div>

                    <div className="chat-window-messages">
                        {messages.map((msg, i) => (
                            <div key={i} className={`message ${msg.role}`}>
                                <div className="bubble-text">
                                    {/* Markdown rendering for better formatting */}
                                    <ReactMarkdown>{msg.content}</ReactMarkdown>
                                </div>
                            </div>
                        ))}
                        {isLoading && (
                            <div className="message assistant">
                                <div className="bubble-text">Thinking...</div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-input-area">
                        <textarea
                            className="chat-input-field"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === 'Enter' && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            placeholder="Ask a question..."
                            disabled={isLoading}
                            rows="1"
                        />
                        <button
                            className="chat-send-button"
                            onClick={handleSend}
                            disabled={isLoading || !input.trim()}
                        >
                            <FaPaperPlane />
                        </button>
                    </div>
                </div>
            )}
            <button className="chat-bubble-button" onClick={() => setIsOpen(!isOpen)}>
                {isOpen ? <FaTimes /> : <FaComments />}
            </button>
        </div>
    );
};

export default FloatingChat;